import { type CandidateStatus } from "@vidario/domain";
import {
  Bookmark,
  CheckCircle,
  Circle,
  Hourglass,
  MessageSquare,
  Mic,
  Send,
  Star,
  Trophy,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { type ReactElement } from "react";

import { cn } from "../utils/cn";

export type { CandidateStatus };

interface StatusConfig {
  label: string;
  icon: LucideIcon;
  classes: string;
}

export const CANDIDATE_STATUS_MAP: Record<CandidateStatus, StatusConfig> = {
  pool: {
    // One word with the admin's tab (#186); "Pool" was internal jargon.
    label: "Aussortiert",
    icon: Circle,
    classes: "bg-[var(--status-pool-bg)] text-[var(--status-pool-fg)]",
  },
  longlist: {
    label: "Longlist",
    icon: Bookmark,
    classes: "bg-[var(--status-longlist-bg)] text-[var(--status-longlist-fg)]",
  },
  selected: {
    label: "Ausgewählt",
    icon: Star,
    classes: "bg-[var(--status-selected-bg)] text-[var(--status-selected-fg)]",
  },
  outreach: {
    label: "Outreach",
    icon: Send,
    classes: "bg-[var(--status-outreach-bg)] text-[var(--status-outreach-fg)]",
  },
  prequal: {
    label: "Vorqualifizierung",
    icon: MessageSquare,
    classes: "bg-[var(--status-prequal-bg)] text-[var(--status-prequal-fg)]",
  },
  interview: {
    label: "Tiefeninterview",
    icon: Mic,
    classes: "bg-[var(--status-interview-bg)] text-[var(--status-interview-fg)]",
  },
  // A recruiter sees an unreleased recommended candidate this way (ADR 0004).
  // Same Qualifizierung tokens as the interview stage it stands in for.
  in_review: {
    label: "In Prüfung",
    icon: Hourglass,
    classes: "bg-[var(--status-interview-bg)] text-[var(--status-interview-fg)]",
  },
  recommendation: {
    label: "Empfehlung",
    icon: CheckCircle,
    classes: "bg-[var(--status-recommendation-bg)] text-[var(--status-recommendation-fg)]",
  },
  placed: {
    label: "Platziert",
    icon: Trophy,
    classes: "bg-[var(--status-placed-bg)] text-[var(--status-placed-fg)]",
  },
  rejected: {
    label: "Abgelehnt",
    icon: XCircle,
    classes: "bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]",
  },
};

export interface StatusBadgeProps {
  status: CandidateStatus;
  size?: "sm" | "md";
  className?: string;
}

export function StatusBadge({ status, size = "md", className }: StatusBadgeProps): ReactElement {
  const cfg = CANDIDATE_STATUS_MAP[status];
  const Icon = cfg.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full py-0.5 pl-2 pr-2.5 font-medium leading-tight",
        size === "sm" ? "text-[11px]" : "text-xs",
        cfg.classes,
        className,
      )}
    >
      <Icon size={12} strokeWidth={2} />
      {cfg.label}
    </span>
  );
}
